/**
 * Visual Sales Tools Service
 */
import { Injectable } from '@angular/core';
import { VSTClientInfo, VSTResult } from '../interfaces/VST';
import { CoverpathControllerInterface } from '../../angular/interfaces/controller/CoverpathControllerInterface';
import { AmorphicService } from '../../angular/services/amorphic/amorphic.service';
import { CoverpathWholeLifeQuote } from '../../classes/quote/coverpath/CoverpathWholeLifeQuote';
import { Utils } from '../../../common/js/Utils';
import { RateClasses } from '../../../common/js/static/RateClasses';
import { IllustrationRequest } from '../../classes/wholelife/IllustrationRequest';
import { CPQuoteRateClassDescriptions, ConceptIllustrationVSTErrors } from '../../js/interfaces/CoverpathTypes';
import { get } from 'lodash';
import { QuoteForm } from '../../../coverpath-agent/public/js/components/home-new/quote-or-apply-new/QuoteForm';
import { Product, RiderType } from '../../../common/js/static/Product';
import { CoverpathPolicy } from '../../classes/coverpath_subclasses/CoverpathPolicy';
import { CoverpathCustomer } from '../../classes/coverpath_subclasses/CoverpathCustomer';
import { ProductClientController } from '../../js/controllers/ProductClientController';
import { SafeInviteInfo } from '../../../common/js/customer/SafeInviteInfo';
import { SafeWholePolicy } from '../../../common/js/customer/SafePolicyInfo';
import { CommonUtils } from '../../../common/js/static/CommonUtils';
import { defaultTaxBracket } from 'apps/common/js/constants/policy';
import { Controller } from '../../../coverpath-agent/public/js/Controller';
import { CoverpathTermQuote } from '../../classes/quote/coverpath/CoverpathTermQuote';

const FALSE_MONOTONICITY_STRING = 'False monotonicity assumption';
const NULL_SOLVER_ERROR = 'Illustration Solver Error: null';

@Injectable({
  providedIn: 'root',
})
export class VSTService {
  private productClientController: ProductClientController;
  private dividendMap = {
    PUA: 'Paid-up Additions',
    Cash: 'Paid in Cash',
    PRPUA: 'Reduce Premium, excess to Paid-Up Additions',
    PRCash: 'Reduce Premium, excess to Cash',
    Accumulate: 'Accumulate at Interest',
  };

  constructor(private amorphicService: AmorphicService) {
    this.productClientController = (
      this.amorphicService.controller as CoverpathControllerInterface
    ).productClientController;
  }

  public getVSTClientData(quote?: QuoteForm, safeInviteInfo?: SafeInviteInfo): VSTClientInfo {
    const customer = this.amorphicService.controller.customer as CoverpathCustomer;
    let primaryPolicy: CoverpathPolicy;
    const hasApplicationAndPolicy = customer.application && customer.application.primaryPolicy;
    if (quote) {
      const { clientInformation, productType } = quote;
      const wholeLifeQuote = productType === 'Whole' ? quote.wholeLifeQuote : quote.jointAppQuote.wholeLifeQuote;
      let rateClass: string = clientInformation.healthClass;
      if (hasApplicationAndPolicy) {
        primaryPolicy = customer.application.primaryPolicy as CoverpathPolicy;
        const faceAndRtrAmount = primaryPolicy.hasRtrRider
          ? primaryPolicy.faceAmount + primaryPolicy.rtrRiderAmount
          : primaryPolicy.faceAmount;
        rateClass = CommonUtils.determineRatingClass(
          clientInformation.healthClass,
          faceAndRtrAmount,
          primaryPolicy.product,
          primaryPolicy.productType
        ).stringCode;
      }
      return {
        dividendOption: this.dividendMap[wholeLifeQuote.dividendOption],
        rateClass: CPQuoteRateClassDescriptions[rateClass].tobaccoDescription,
      };
    } else {
      let rateClass;
      let dividendOption;
      const cpQuote: CoverpathWholeLifeQuote = customer.wholeLifeQuote;
      const cpTermQuote: CoverpathTermQuote = customer.termQuote;
      const productSwitchedFromTermToWhole = safeInviteInfo?.hasTermQuote && safeInviteInfo?.safePolicyInfo.hasWhole;
      if (cpQuote && !hasApplicationAndPolicy) {
        rateClass = cpQuote.healthClass;
        dividendOption = cpQuote.dividend;
      } else if (productSwitchedFromTermToWhole && !hasApplicationAndPolicy) {
        rateClass = customer.termQuote.healthClass;
        dividendOption = safeInviteInfo.safePolicyInfo.primaryPolicy.dividendOption;
      } else if ((cpQuote || productSwitchedFromTermToWhole) && hasApplicationAndPolicy) {
        primaryPolicy = customer.application.primaryPolicy as CoverpathPolicy;
        const faceAndRtrAmount = primaryPolicy.hasRtrRider
          ? primaryPolicy.faceAmount + primaryPolicy.rtrRiderAmount
          : primaryPolicy.faceAmount;
        rateClass = CommonUtils.determineRatingClass(
          productSwitchedFromTermToWhole ? cpTermQuote.healthClass : cpQuote.healthClass,
          faceAndRtrAmount,
          primaryPolicy.product,
          primaryPolicy.productType
        ).stringCode;
        dividendOption = primaryPolicy.dividendOption;
      } else if (safeInviteInfo) {
        rateClass = RateClasses.find(safeInviteInfo.safePolicyInfo.primaryPolicy.healthClass).stringCode;
        dividendOption = safeInviteInfo.safePolicyInfo.primaryPolicy.dividendOption;
      }
      return {
        dividendOption: this.dividendMap[dividendOption],
        rateClass: CPQuoteRateClassDescriptions[rateClass].tobaccoDescription,
      };
    }
  }

  private createConceptIllustrationVSTResult(vstData, isSolve: boolean, startAge, endAge): VSTResult | null {
    const policy = get(vstData, 'data.illustration.payload.policy');
    if (policy) {
      const yearZero = policy.illustrationList[0];
      let cumulativeIncome =
        isSolve && yearZero.ageEndYear - 1 > startAge && yearZero.ageEndYear <= endAge
          ? Math.abs(yearZero.annualNetOutlayBegYear)
          : 0;
      let highestBenefitYearIndex,
        highestBenefit: number = 0;
      const vstResult: VSTResult = {
        ages: [yearZero.ageEndYear - 1],
        currentPaidPremiums: [0],
        totalDeathBenefits: {
          name: 'Total Death Benefit, Non-Guaranteed',
          dashStyle: 'Solid',
          color: '#009CDE',
          tooltip: 'Placeholder Copy',
          data: [Math.round(yearZero.totalDeathBenefitEndYear)],
        },
        totalCashValues: {
          name: 'Net Cash Value',
          dashStyle: 'ShortDash',
          color: '#86B3FD',
          tooltip: 'Placeholder Copy',
          data: [Math.round(yearZero.netCashValueEndYear)], //HL-40131: map cash value to netCashValueEndYear
        },
        guaranteedDeathBenefits: {
          name: 'Guaranteed, Death Benefit',
          dashStyle: 'ShortDot',
          color: '#009CDE',
          tooltip: 'Placeholder Copy',
          data: [Math.round(yearZero.guaranteedDeathBenefitEndYear)],
        },
        guaranteedCashValues: {
          name: 'Guaranteed, Cash Value',
          dashStyle: 'ShortDot',
          color: '#F5E500',
          tooltip: 'Placeholder Copy',
          data: [Math.round(yearZero.guaranteedCashValueEndYear)],
        },
        accumulatedPaidPremium: {
          name: 'Cumulative Contract Premium',
          dashStyle: 'Solid',
          color: '#43B02A',
          tooltip: 'Placeholder Copy',
          data: [0],
        },
        mecYear: policy.mecYear,
        annualDividendEndYear: {
          name: 'Annual Dividend End Year',
          dashStyle: 'ShortDot',
          color: '#F5E500',
          tooltip: 'Placeholder Copy',
          data: [Math.round(yearZero.annualDividendEndYear)],
        },
        paidUpAdditionsEndYear: {
          name: 'Paid Up Additions End Year',
          dashStyle: 'ShortDot',
          color: '#F5E500',
          tooltip: 'Placeholder Copy',
          data: [Math.round(yearZero.paidUpAdditionsEndYear)],
        },
        netOutlay: {
          name: 'Net Outlay',
          dashStyle: 'Solid',
          color: '#FF5733',
          tooltip: 'Placeholder Copy',
          data: [isSolve ? Math.round(yearZero.annualNetOutlayBegYear) : null],
        },
        surrenderAmount: {
          name: 'Surrender Amount',
          dashStyle: 'Solid',
          color: '#FF5733',
          tooltip: 'Placeholder Copy',
          data: [isSolve ? Math.round(yearZero.withdrawAmount) : null],
        },
        annualLoan: {
          name: 'Annual Loan',
          dashStyle: 'Solid',
          color: '#FF5733',
          tooltip: 'Placeholder Copy',
          data: [isSolve ? Math.round(yearZero.annualLoanRequest + yearZero.uncapitalizedLoanInterest) : null],
        },
        netSurrenderValue: {
          name: 'Net Surrender Value',
          dashStyle: 'Solid',
          color: '#FF5733',
          tooltip: 'Placeholder Copy',
          data: [Math.round(yearZero.totalCashValueEndYear - yearZero.totalOutstandingLoanEndYear)],
        },
        netDeathBenefit: {
          name: 'Net Death Benefit',
          dashStyle: 'ShortDash',
          color: '#F99191',
          tooltip: 'Placeholder Copy',
          data: [Math.round(yearZero.netDeathBenefitEndYear)],
        },
        solvedValue: get(vstData, 'data.illustration.payload.solver.solvedValue'),
        combinedTotalBenefit: {
          name: 'Combined Total Benefit',
          dashStyle: 'Solid',
          color: '#99DCDE',
          tooltip: 'Placeholder Copy',
          data: [
            isSolve
              ? Math.round(yearZero.totalDeathBenefitEndYear - yearZero.totalOutstandingLoanEndYear + cumulativeIncome)
              : null,
          ],
        },
        cumulativeRetirementIncome: {
          name: 'Cumulative Retirement Income',
          dashStyle: 'Solid',
          color: '#ffffff00',
          tooltip: 'Placeholder Copy',
          data: [isSolve ? Math.round(cumulativeIncome) : null],
        },
        totalIncome: 0,
      };

      if (!isNaN(vstResult.combinedTotalBenefit.data[0])) {
        highestBenefit = vstResult.combinedTotalBenefit.data[0];
        highestBenefitYearIndex = 0;
      }

      const dataSet = policy.illustrationList;
      for (let i = 0; i < dataSet.length; i++) {
        vstResult.ages.push(dataSet[i].ageEndYear);
        vstResult.totalDeathBenefits.data.push(Math.round(dataSet[i].totalDeathBenefitEndYear));
        vstResult.totalCashValues.data.push(Math.round(dataSet[i].netCashValueEndYear)); //HL-40131: map cash value to netCashValueEndYear
        vstResult.guaranteedDeathBenefits.data.push(Math.round(dataSet[i].guaranteedDeathBenefitEndYear));
        vstResult.guaranteedCashValues.data.push(Math.round(dataSet[i].guaranteedCashValueEndYear));
        vstResult.currentPaidPremiums.push(dataSet[i].contractPremiumBegYear);
        const premium =
          vstResult.accumulatedPaidPremium.data[i] + Math.round(dataSet[i].guaranteedContractPremiumBegYear);
        vstResult.accumulatedPaidPremium.data.push(premium);

        vstResult.annualDividendEndYear.data.push(dataSet[i].annualDividendEndYear);
        vstResult.paidUpAdditionsEndYear.data.push(dataSet[i].paidUpAdditionsEndYear);
        vstResult.netOutlay.data.push(isSolve ? dataSet[i].annualNetOutlayBegYear : null);
        vstResult.surrenderAmount.data.push(isSolve ? dataSet[i].withdrawAmount : null);
        vstResult.annualLoan.data.push(
          isSolve ? dataSet[i].annualLoanRequest + dataSet[i].uncapitalizedLoanInterest : null
        );
        vstResult.netSurrenderValue.data.push(
          dataSet[i].totalCashValueEndYear - dataSet[i].totalOutstandingLoanEndYear
        );

        const netDeathBenefitValue = dataSet[i].netDeathBenefitEndYear;
        vstResult.netDeathBenefit.data.push(netDeathBenefitValue);
        if (isSolve) {
          const ageEndYear = dataSet[i].ageEndYear;
          if (ageEndYear > startAge && ageEndYear <= endAge) {
            cumulativeIncome += Math.abs(dataSet[i].annualNetOutlayBegYear);
          }
          vstResult.cumulativeRetirementIncome.data.push(cumulativeIncome);
          vstResult.combinedTotalBenefit.data.push(netDeathBenefitValue + cumulativeIncome);
          if (vstResult.combinedTotalBenefit.data[i] > highestBenefit) {
            highestBenefit = vstResult.combinedTotalBenefit.data[i];
            highestBenefitYearIndex = i;
          }
        }
      }
      vstResult.totalIncome = isSolve ? cumulativeIncome : 0;
      // if no solver is involved, then use the original logic that assumes the age 100 is when benefit is at the highest.
      vstResult.highestBenefitAgeIndex = isSolve ? highestBenefitYearIndex : 100 - vstResult.ages[0];
      return vstResult;
    }
  }
  public async getVSTData(quote?: QuoteForm, safeInviteInfo: SafeInviteInfo = null): Promise<VSTResult | null> {
    return (this.amorphicService.controller as Controller).getVSTData(quote, safeInviteInfo);
  }

  private createConceptIllustrationVSTRequest(insuredDetails, illustrationDetails): IllustrationRequest {
    const currentDate: Date = new Date();
    return {
      requestId: Utils.generateUuid(),
      dividendInterestAdjustment: illustrationDetails.dividendInterestAdjustment,
      dividendOption: illustrationDetails.dividendOption,
      startDate: currentDate,
      gender: insuredDetails.gender === 'Male' ? '1' : '2',
      dob: insuredDetails.dateOfBirth,
      productCode: illustrationDetails.product,
      state: insuredDetails.state,
      rateClass: illustrationDetails.healthClass,
      paymentFrequency: illustrationDetails.paymentFrequency, // check the correct value for this
      faceAmount: illustrationDetails.faceAmount,
      waiverUnits: illustrationDetails.waiverOfPremium ? illustrationDetails.faceAmount : 0,
      flatExtra: 1, // check value
      taxRate: Utils.round(illustrationDetails.taxBracket / 100, 2),
      loanRateType: illustrationDetails.loanInterestType,
      dividendRatePercent: 1,
      agent: null,
      customerPerson: null,
      riders: [],
      startAge: illustrationDetails.startAge,
      endAge: illustrationDetails.endAge,
    };
  }

  public async getIllustrationTabVSTData(insuredDetails, illustrationDetails, isSolve: boolean) {
    const request = this.createConceptIllustrationVSTRequest(insuredDetails, illustrationDetails);
    const data = await this.productClientController.getVSTData(request, true, isSolve);

    if (data) {
      if (data.errors && (data.errors.includes(FALSE_MONOTONICITY_STRING) || data.errors.includes(NULL_SOLVER_ERROR))) {
        return { error: ConceptIllustrationVSTErrors.START_AGE };
      }

      return this.createConceptIllustrationVSTResult(
        data,
        isSolve,
        illustrationDetails.startAge,
        illustrationDetails.endAge
      );
    }
  }
}
